import { gql, useQuery } from '@apollo/client';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import IconVariant from '@apollo/icons/default/IconVariant.svg';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Heading,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@apollo/orbit';
import { Moon } from '@apollo/space-kit/illustrations/Moon';
import _ from 'lodash';
import React from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import {
  graphPersistedQueriesRouteConfig,
  persistedQueryListRouteConfig,
} from 'src/app/graph/routes';
import { CollapsibleText } from 'src/components/common/collapsibleText/CollapsibleText';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { TruncateWithTooltip } from 'src/components/TruncateWithTooltip';
import { useRouteParams } from 'src/hooks/useRouteParams';
import config from 'src/lib/config';
import { format } from 'src/lib/format';
import {
  PersistedQueriesHomePageQuery,
  PersistedQueriesHomePageQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { PersistedQueryListActionsMenu } from './persistedQueryListActionMenu/PersistedQueryListActionMenu';
import { PersistedQueryListConfigWizard } from './persistedQueryListConfigWizard/PersistedQueryListConfigWizard';
import { persistedQueryListDetailsFragment } from './PersistedQueryListDetailsFragment';

const persistedQueriesHomePageQuery = gql<
  PersistedQueriesHomePageQuery,
  PersistedQueriesHomePageQueryVariables
>`
  query PersistedQueriesHomePageQuery($graphId: ID!) {
    graph(id: $graphId) {
      id
      title
      persistedQueryLists {
        id
        ...PersistedQueryListDetailsFragment
      }
    }
  }
  ${persistedQueryListDetailsFragment}
`;

export const PersistedQueriesHomePage = () => {
  const { graphId } = useRouteParams(graphPersistedQueriesRouteConfig);
  const location = useLocation();

  const shouldShowNewPQsUI = useLDFlag('gov-pq-ux-sort-filter-delete');

  const { data, loading, error } = useQuery(persistedQueriesHomePageQuery, {
    variables: {
      graphId,
    },
  });

  const listCount = data?.graph?.persistedQueryLists?.length ?? 0;
  const lists = _.orderBy(
    data?.graph?.persistedQueryLists ?? [],
    (list) => list.createdAt,
    'asc',
  );

  return loading ? (
    <Loading />
  ) : !data?.graph?.persistedQueryLists ? (
    // TODO(tim): handle plan / user capabilities / not found vs. loading errors differently
    <NotFound />
  ) : error ? (
    <ErrorPage />
  ) : (
    <>
      {shouldShowNewPQsUI ? (
        <PageTitle
          title="Persisted query lists"
          tabs={
            <Text variant="subtitle" className="mb-3">
              Use persisted queries to enforce a safelist on your variants or to
              manage a list of short query IDs that can be used in place of full
              freeform GraphQL in requests.{' '}
              <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                Learn more
              </Link>
            </Text>
          }
          {...(listCount > 0
            ? {
                rightSide: (
                  <PermissionGuard
                    graphId={graphId}
                    graphPermissions="canManagePersistedQueryLists"
                    fallback={
                      <Tooltip
                        placement="bottom-end"
                        label="Ask a graph administrator if you need to make a new list."
                      >
                        <Button isDisabled>Create new List</Button>
                      </Tooltip>
                    }
                  >
                    <Button
                      as={ReactRouterLink}
                      to={setOverlayVisibleLocation({
                        location,
                        overlayName:
                          config.modals.configurePersistedQueriesWizard,
                      })}
                      isDisabled={loading}
                    >
                      Create new list
                    </Button>
                  </PermissionGuard>
                ),
              }
            : {})}
        >
          Persisted query lists{' '}
          {listCount > 0 && (
            <Badge variant="neutralOutlined" className="align-[5px]">
              {listCount}
            </Badge>
          )}
        </PageTitle>
      ) : (
        <PageTitle
          title={`Persisted Queries for ${data?.graph?.title ?? '--'}`}
        />
      )}

      {lists.length > 0 ? (
        <Card className="m-8">
          {!shouldShowNewPQsUI && (
            <CardHeader>
              <CardTitle>
                Persisted query lists {listCount > 0 ? ` (${listCount})` : ''}
              </CardTitle>
              <CardDescription>
                Use persisted queries to enforce a safelist on your variants or
                to manage a list of short query IDs that can be used in place of
                full freeform GraphQL in requests. Learn more in the{' '}
                <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                  docs
                </Link>
                .
              </CardDescription>
              <PermissionGuard
                graphId={graphId}
                graphPermissions="canManagePersistedQueryLists"
                fallback={
                  <Tooltip
                    placement="bottom-end"
                    label="Ask a graph administrator if you need to make a new list."
                  >
                    <Button isDisabled>New List</Button>
                  </Tooltip>
                }
              >
                <Button
                  as={ReactRouterLink}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: config.modals.configurePersistedQueriesWizard,
                  })}
                  isDisabled={loading}
                >
                  New list
                </Button>
              </PermissionGuard>
            </CardHeader>
          )}
          <CardBody>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th className="w-64">Name</Th>
                    {/* Included to allow the first column to have a min width */}
                    <Th />
                    <Th className="w-[200px]">Created</Th>
                    <Th className="w-[200px] whitespace-nowrap">
                      Last published
                    </Th>
                    <Th className="w-[200px]">Size</Th>
                    <Th className="w-1/5 whitespace-nowrap">Linked variants</Th>
                    <Th isActionsMenu className="w-[110px]">
                      <span className="sr-only">Actions</span>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {lists.map((list) => {
                    return (
                      <Tr key={list.id}>
                        <Td className="whitespace-normal" colSpan={2}>
                          <p className="truncate">
                            <Link
                              to={persistedQueryListRouteConfig.location({
                                graphId,
                                listId: list.id,
                              })}
                            >
                              {list.name}
                            </Link>
                          </p>
                          <CollapsibleText
                            maxLines={2}
                            className="mt-2 leading-tight"
                          >
                            <span className="text-secondary">
                              {list.description}
                            </span>
                          </CollapsibleText>
                        </Td>
                        <Td>
                          <RelativeTimestamp value={list.createdAt} />
                        </Td>
                        <Td>
                          {list.currentBuild.publish?.publishedAt ? (
                            <RelativeTimestamp
                              value={list.currentBuild.publish?.publishedAt}
                            />
                          ) : null}
                        </Td>
                        <Td>
                          <p className="truncate">
                            {list.currentBuild.totalOperationsInList === 0 ? (
                              <Tooltip label="This list does not have any published operations">
                                {shouldShowNewPQsUI ? (
                                  '-'
                                ) : (
                                  <IconUnavailable className="text-icon-disabled" />
                                )}
                              </Tooltip>
                            ) : (
                              `${
                                list.currentBuild.totalOperationsInList
                              } ${format.pluralize(
                                list.currentBuild.totalOperationsInList,
                                'operation',
                              )}`
                            )}
                          </p>
                        </Td>
                        <Td>
                          <div className="flex flex-wrap gap-x-2">
                            {list.linkedVariants.map((v) => (
                              <div key={v.id} className="flex items-center">
                                <IconVariant className="mr-2 text-icon-primary" />
                                <TruncateWithTooltip content={v.name} />
                              </div>
                            ))}
                          </div>
                        </Td>
                        <Td isActionsMenu>
                          <PersistedQueryListActionsMenu
                            graphId={graphId}
                            list={list}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      ) : (
        // TODO(evan-pq): This should match the new empty state in the variant page
        <Card className="m-8 flex h-full">
          <CardBody className="items-center justify-center text-center">
            <div className="my-20">
              <Moon />
              <Heading as="h1" size="xl" className="mt-8">
                Create your first persisted query list
              </Heading>
              <div className="mt-2 max-w-120 text-secondary">
                Use persisted queries to enforce a safelist on your variants or
                to manage a list of short query IDs that can be used in place of
                full freeform GraphQL in requests. Learn more in the{' '}
                <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                  docs
                </Link>
                .
              </div>
              <Button
                className="mt-8"
                as={ReactRouterLink}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: config.modals.configurePersistedQueriesWizard,
                })}
                isLoading={loading}
                loadingText="Creating"
              >
                Create a persisted query list
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
      <LazyUrlModal name={config.modals.configurePersistedQueriesWizard}>
        {({ isOpen, closeModal }) => (
          <PersistedQueryListConfigWizard
            isOpen={isOpen}
            onClose={closeModal}
          />
        )}
      </LazyUrlModal>
    </>
  );
};
